import type { ComponentProps, PropsWithChildren } from 'react';
import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import {
  bottom,
  left as leftUtil,
  position,
  right as rightUtil,
  zIndex as zIndexUtil,
} from 'styled-system';
import type { BottomProps, LeftProps, PositionProps, RightProps, ZIndexProps } from 'styled-system';

import { STICKY_CONTAINER_BOTTOM_Z_INDEX } from '../constants/z-index';
import type { Viewport } from '../enums/Viewport';
import MatchedViewport from './MatchedViewport';
import OnWindowScroll from './OnWindowScroll';

const StickyTop = styled.div<any>`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
`;

const StickyBottom = styled.div<PositionProps & RightProps & LeftProps & BottomProps & ZIndexProps>`
  ${position};
  ${rightUtil};
  ${leftUtil};
  ${bottom};
  ${zIndexUtil};
`;

const containerStyles: Partial<ComponentProps<typeof StickyBottom>> = {
  position: 'fixed',
  bottom: 0,
};

interface StickyDependentProps extends PropsWithChildren, ZIndexProps, RightProps, LeftProps {
  dependsOn: string;
  additionalElementOffset?: number;
  viewport: Viewport;
}

function getViewportHeight(): number {
  return 'visualViewport' in window && window.visualViewport !== null
    ? window.visualViewport.height
    : window.innerHeight;
}

const StickyDependent = ({
  viewport,
  children,
  additionalElementOffset,
  dependsOn,
}: StickyDependentProps) => {
  const [isSticky, setIsSticky] = useState(false);

  const onScroll = useCallback(() => {
    const element = document.querySelector(dependsOn);

    if (element === null) {
      return;
    }

    const windowPageYOffset = window.pageYOffset;
    const { top } = element.getBoundingClientRect();
    const elementTopOffset = windowPageYOffset + top;
    const { clientHeight } = element;
    const additionalOffset =
      additionalElementOffset > 0 ? clientHeight + additionalElementOffset : 0;

    const isElementVisible =
      windowPageYOffset + getViewportHeight() > elementTopOffset + additionalOffset;

    setIsSticky(!isElementVisible);
  }, [setIsSticky, additionalElementOffset, dependsOn]);

  return (
    <MatchedViewport viewport={viewport}>
      <OnWindowScroll onScroll={onScroll}>{isSticky && children}</OnWindowScroll>
    </MatchedViewport>
  );
};

export const StickyTopDependent = ({
  children,
  viewport,
  dependsOn,
  additionalElementOffset = 0,
}: StickyDependentProps) => (
  <StickyDependent
    viewport={viewport}
    dependsOn={dependsOn}
    additionalElementOffset={additionalElementOffset}
  >
    <StickyTop>{children}</StickyTop>
  </StickyDependent>
);

export const StickyBottomDependent = ({
  children,
  viewport,
  dependsOn,
  additionalElementOffset = 0,
  left = 0,
  right = 0,
  zIndex = STICKY_CONTAINER_BOTTOM_Z_INDEX,
}: StickyDependentProps) => (
  <StickyDependent
    viewport={viewport}
    dependsOn={dependsOn}
    additionalElementOffset={additionalElementOffset}
  >
    <StickyBottom {...containerStyles} left={left} right={right} zIndex={zIndex}>
      {children}
    </StickyBottom>
  </StickyDependent>
);
